import React, { useState, useRef, useEffect } from 'react';

const SearchableDropdown = ({ 
  options = [], 
  value = '', 
  onChange, 
  placeholder = 'Select option...', 
  disabled = false,
  className = '',
  searchPlaceholder = 'Search...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = options.filter(option =>
        option.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Focus search input when dropdown opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm('');
    }
  };

  const handleOptionSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    } else if (e.key === 'Enter' && filteredOptions.length === 1) {
      handleOptionSelect(filteredOptions[0]);
    }
  };

  const displayValue = value || placeholder;
  const hasValue = Boolean(value);

  return (
    <div className={`position-relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        className={`form-select text-start ${disabled ? 'disabled' : ''}`}
        onClick={handleToggle}
        disabled={disabled}
        style={{
          backgroundColor: disabled ? '#e9ecef' : 'white',
          color: hasValue ? '#212529' : '#6c757d'
        }}
      >
        <span className="text-truncate">{displayValue}</span>
      </button>

      {isOpen && (
        <div
          className="position-absolute w-100 border rounded bg-white shadow-lg"
          style={{
            top: '100%',
            zIndex: 1050,
            maxHeight: '300px',
            overflow: 'hidden'
          }}
        >
          <div className="p-2 border-bottom">
            <input
              ref={searchInputRef}
              type="text"
              className="form-control form-control-sm"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
            {value && (
              <button
                type="button"
                className="dropdown-item text-muted"
                onClick={() => handleOptionSelect('')}
                style={{ fontStyle: 'italic' }}
              >
                {placeholder}
              </button>
            )}
            
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <button
                  key={`${option}-${index}`}
                  type="button"
                  className={`dropdown-item ${option === value ? 'active' : ''}`}
                  onClick={() => handleOptionSelect(option)}
                  style={{ 
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {option}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-muted">
                {searchTerm ? `No results found for "${searchTerm}"` : 'No options available'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
